'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { Room, RoomEvent, Track, ConnectionState } from 'livekit-client';
import { Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { formatDuration } from '@/app/call/room/types';
import { notify } from '@/lib/notifications';
import { authService } from '@/lib/authService';
import { desktopNotifications } from '@/lib/desktopNotifications';
import { useSupportSession } from '@/hooks/useSupportSession';

/**
 * Phase 10.3: Remote Support Receiver
 *
 * Mounted in SessionProvider. Listens for support session events via
 * useSupportSession. When a support_session_started event arrives:
 *
 * 1. Shows notify.confirm dialog asking the user to grant screen access
 * 2. If accepted, calls /api/v1/support/sessions/[id]/browser-share
 * 3. Gets a LiveKit token with publish permissions
 * 4. Calls getDisplayMedia() to capture the screen
 * 5. Joins the LiveKit room and publishes the screen track
 * 6. Shows a "Screen sharing in progress" indicator with Stop button
 *
 * For agent-based sessions (session_mode='agent'), this component is a
 * no-op — the agent handles consent and screen capture natively.
 */

interface ActiveShare {
  sessionId: string;
  roomName: string;
  room: Room;
  stream: MediaStream;
}

export function RemoteSupportReceiver({ userId, enabled }: { userId: number | null | undefined; enabled: boolean }) {
  const { activeSession, endedEvent, endSession } = useSupportSession({ userId, enabled });
  const [activeShare, setActiveShare] = useState<ActiveShare | null>(null);
  const [sharing, setSharing] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const consentShownRef = useRef<Set<string>>(new Set());
  const activeShareRef = useRef<ActiveShare | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);

  // Keep ref in sync so stopScreenShare can access the latest value
  useEffect(() => {
    activeShareRef.current = activeShare;
  }, [activeShare]);

  const stopScreenShare = useCallback(async (sessionId?: string) => {
    const current = activeShareRef.current;
    const targetSessionId = sessionId || current?.sessionId;
    if (!targetSessionId) return;

    // Stop the stream
    if (current?.stream) {
      current.stream.getTracks().forEach((t) => t.stop());
    }

    // Leave the LiveKit room
    if (current?.room) {
      await current.room.disconnect();
    }

    // Notify the portal
    const tokens = authService.getTokens();
    const token = tokens?.access_token || '';
    try {
      await fetch(`/api/v1/support/sessions/${targetSessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 'ended', end_reason: 'user_stopped' }),
      });
    } catch {
      // Non-critical
    }

    setActiveShare(null);
    setSharing(false);
    setMicEnabled(false);
    setSpeakerMuted(false);
    setElapsedSeconds(0);
    endSession();
    notify.info('Screen sharing stopped');
  }, [endSession]);

  // Mute/unmute the technician's audio on our side.
  const toggleSpeaker = useCallback(() => {
    setSpeakerMuted((prev) => {
      const next = !prev;
      if (remoteAudioRef.current) remoteAudioRef.current.muted = next;
      return next;
    });
  }, []);

  // Share duration ticker — runs while a share is active.
  useEffect(() => {
    if (!sharing) return;
    const startedAt = Date.now();
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [sharing]);

  // Opt-in voice: publish the user's mic to the technician. Off by default —
  // each side presses the mic button to speak.
  const toggleMic = useCallback(async () => {
    const room = activeShareRef.current?.room;
    if (!room) return;
    try {
      await room.localParticipant.setMicrophoneEnabled(!micEnabled);
      setMicEnabled(!micEnabled);
    } catch (err) {
      console.warn('[RemoteSupportReceiver] mic toggle failed:', err);
      notify.error('Could not access the microphone');
    }
  }, [micEnabled]);

  const startScreenShare = useCallback(async (sessionId: string, livekitToken: string, livekitHost: string, roomName: string, withAudio = false) => {
    try {
      // Capture screen
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });

      // Listen for the user stopping screen share via browser UI
      stream.getVideoTracks()[0]?.addEventListener('ended', () => {
        stopScreenShare(sessionId);
      });

      // Join LiveKit room — subscribe to the technician's audio if they
      // opt in to voice (attached to the hidden <audio> element below).
      const room = new Room();
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Audio && remoteAudioRef.current) {
          track.attach(remoteAudioRef.current);
        }
      });
      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) track.detach();
      });
      await room.connect(livekitHost, livekitToken);

      // Publish screen track
      const screenTrack = stream.getVideoTracks()[0];
      if (screenTrack) {
        await room.localParticipant.publishTrack(screenTrack, {
          name: 'screen-share',
          source: Track.Source.ScreenShare,
        });
      }

      // Voice was pre-consented in the dialog — enable the mic now. Failure
      // (no mic, permission denied) degrades to screen-only sharing.
      if (withAudio) {
        try {
          await room.localParticipant.setMicrophoneEnabled(true);
          setMicEnabled(true);
        } catch (micErr) {
          console.warn('[RemoteSupportReceiver] mic enable failed:', micErr);
          notify.info('Voice could not be enabled — continuing with screen only');
        }
      }

      setActiveShare({ sessionId, roomName, room, stream });
      setSharing(true);
      notify.success('Screen sharing started. The technician can now view your screen.');
    } catch (err) {
      console.error('[RemoteSupportReceiver] Screen share error:', err);
      notify.error(err instanceof Error ? err.message : 'Failed to start screen sharing');
      endSession();
    }
  }, [endSession, stopScreenShare]);

  const handleSupportRequest = useCallback(async (session: { sessionId: string; roomName: string; technicianName?: string }) => {
    const techName = session.technicianName || 'A Total Access support technician';
    // Attention sound — plays even when the FCM web-push path isn't
    // granted (desktopNotifications uses plain Audio, no permission needed).
    desktopNotifications.playSound('system');
    // 3-button consent dialog: Deny | Allow Screen Only | Allow Screen + Voice
    let withAudio = false;
    const accepted = await notify.confirm({
      title: 'Remote Support Request',
      message: `${techName} is requesting to view your screen. They will be able to see your screen but cannot control your mouse or keyboard. Choose whether to also enable two-way voice.`,
      confirmText: 'Allow Screen + Voice',
      secondaryConfirmText: 'Allow Screen Only',
      cancelText: 'Deny',
      variant: 'info',
      onConfirm: () => { withAudio = true; },
      onSecondaryConfirm: () => { withAudio = false; },
    });

    const tokens = authService.getTokens();
    const token = tokens?.access_token || '';

    try {
      const resp = await fetch(`/api/v1/support/sessions/${session.sessionId}/browser-share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          accepted,
          granted_permissions: accepted ? (withAudio ? ['view', 'audio'] : ['view']) : [],
        }),
      });

      const data = await resp.json();
      if (!data.success) {
        notify.error(data.error || 'Failed to respond to support request');
        return;
      }

      if (!accepted) {
        notify.info('Support request denied');
        endSession();
        return;
      }

      // User accepted — start screen share (mic auto-enables if they
      // chose "Allow Screen + Voice" in the consent dialog)
      await startScreenShare(session.sessionId, data.token, data.livekit_host, data.room_name, withAudio);
    } catch (err) {
      console.error('[RemoteSupportReceiver] Error:', err);
      notify.error('Failed to process support request');
      endSession();
    }
  }, [endSession, startScreenShare]);

  // Handle incoming support session request
  useEffect(() => {
    if (!activeSession || !enabled) return;
    // Only handle browser-mode sessions here; agent sessions are handled by the agent
    // The session_mode isn't in the WebSocket event, so we handle all sessions here
    // and let the API reject if it's an agent session
    if (consentShownRef.current.has(activeSession.sessionId)) return;
    consentShownRef.current.add(activeSession.sessionId);

    // Defer to microtask — handleSupportRequest calls setState after await,
    // but the static analyzer flags it. queueMicrotask breaks the sync chain.
    queueMicrotask(() => {
      handleSupportRequest({ ...activeSession }).catch(console.error);
    });
  }, [activeSession, enabled, handleSupportRequest]);

  // FCM path — the Go WebSocket service sends a visible push
  // (type=support_session_request) for new consent requests, and data-only
  // pushes for session end/reject. capacitorDeviceService dispatches these
  // as window events so the same notify.confirm consent flow runs when the
  // request arrives via push rather than WebSocket.
  useEffect(() => {
    if (!enabled) return;

    const onRequest = (event: WindowEventMap['supportSessionRequest']) => {
      const sessionId = event.detail?.session_id;
      const roomName = event.detail?.room_name;
      if (!sessionId || !roomName) return;
      if (consentShownRef.current.has(sessionId)) return;
      consentShownRef.current.add(sessionId);
      queueMicrotask(() => {
        handleSupportRequest({ sessionId, roomName, technicianName: event.detail?.technician_name }).catch(console.error);
      });
    };

    const onEnded = (event: WindowEventMap['supportSessionEnded']) => {
      const current = activeShareRef.current;
      if (!current) return;
      const sessionId = event.detail?.session_id;
      if (sessionId && sessionId !== current.sessionId) return;
      stopScreenShare(current.sessionId);
    };

    window.addEventListener('supportSessionRequest', onRequest);
    window.addEventListener('supportSessionEnded', onEnded);
    return () => {
      window.removeEventListener('supportSessionRequest', onRequest);
      window.removeEventListener('supportSessionEnded', onEnded);
    };
  }, [enabled, handleSupportRequest, stopScreenShare]);

  // Stop sharing when the session is ended remotely (technician ended it,
  // TTL expired, or it was revoked via the API).
  useEffect(() => {
    if (!endedEvent) return;
    const current = activeShareRef.current;
    if (!current) return;
    // If the event carries a session id, only react when it matches ours.
    if (endedEvent.sessionId && endedEvent.sessionId !== current.sessionId) return;
    stopScreenShare(current.sessionId);
  }, [endedEvent, stopScreenShare]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activeShareRef.current?.stream) {
        activeShareRef.current.stream.getTracks().forEach((t) => t.stop());
      }
      if (activeShareRef.current?.room) {
        activeShareRef.current.room.disconnect();
      }
    };
  }, []);

  return (
    <>
      {/* Technician's microphone audio — rendered unconditionally so the ref
          exists before remote tracks can subscribe */}
      <audio ref={remoteAudioRef} autoPlay className="hidden" />
      {sharing && activeShare && (
        <div className="fixed bottom-4 right-4 z-[200] bg-red-600 text-white px-4 py-3 rounded-lg shadow-2xl flex items-center gap-3">
          <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
          <div>
            <div className="font-semibold text-sm">Screen sharing in progress</div>
            <div className="text-xs text-white/80">
              A technician is viewing your screen · {formatDuration(elapsedSeconds)}
            </div>
          </div>
          <button
            onClick={toggleMic}
            title={micEnabled ? 'Mute microphone' : 'Enable voice'}
            className="ml-2 p-2 bg-white/20 hover:bg-white/30 rounded-md transition-colors"
          >
            {micEnabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
          </button>
          <button
            onClick={toggleSpeaker}
            title={speakerMuted ? 'Unmute technician audio' : 'Mute technician audio'}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-md transition-colors"
          >
            {speakerMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => stopScreenShare()}
            className="px-3 py-1.5 bg-white text-red-600 rounded-md text-sm font-medium hover:bg-red-50 transition-colors"
          >
            Stop Sharing
          </button>
        </div>
      )}
    </>
  );
}
